// React Dependencies
import React, { useEffect, useState, useReducer, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import { Dialog, Input } from '@material-ui/core';

// Internal Dependencies
import Colours from '../../../styles/colours';
import ApiService from '../../../services/apiService';
import pageAction from '../../../actions/page';
import Tooltip from '../../../components/Common/ToolTip';
import Icons from '../../../components/Common/Icons';
import OverLoading from '../../../components/Loading';
import PhotoCropper from '../../../components/Common/photos/PhotoCropper';
import Constants, { IMAGE_FORMATS } from '../../../common/constants';
import { getFileDataUrl, IsEmpty } from '../../../common/util';
import MText from '../../../components/Common/MText';

const defaultCropperState = {
  isCropping: false,
  file:       null,
  src:        null,
};

const maxFileSize = 5; // MB

const PageLogo = (props) => {
  const inputRef = useRef();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState('');
  const [cropper, setCropper] = useReducer(
    (state, action) => ({ ...state, ...action }), defaultCropperState,
  );

  const validateImage = (file) => {
    if (!IsEmpty(file)) {
      if (!IMAGE_FORMATS.includes(file.type)) {
        toast.error('Only JPG, JPEG and PNG files are supported.');
        return false;
      }
      if (file.size > Constants.BinaryOneMBSize * maxFileSize) {
        toast.error(`Maximum file size of ${maxFileSize}MB exceeded.`);
        return false;
      }
      return true;
    }
  };

  const handleImageChange = (e) => {
    try {
      const fileInput = e.target;
      if (fileInput.files.length === 0) {
        return;
      }
      const file = fileInput.files[0];
      if (!validateImage(file)) {
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropper({ isCropping: true, file, src: reader.result });
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error(err.message);
      console.log('Error at PageLogo.handleSave():', err);
    }
  };

  const handleUpload = async (file) => {
    try {
      if (!IsEmpty(file)) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('mediaType', 'Logo');

        const cloudfrontLink = await ApiService.uploadPageMedia(props.pageId, formData);

        if (cloudfrontLink && cloudfrontLink.length > 0) {
          props.setLogo(cloudfrontLink);
          const pageIndex = props.pages.findIndex((el) => el.id === props.page.id);
          props.pages[pageIndex].pageLogo = cloudfrontLink;
          props.dispatchPagesAccess(props.pages);
          props.dispatchPageSelected({ ...props.page, pageLogo: cloudfrontLink });

          toast.success('Image uploaded successfully.');
        } else {
          toast.error('An unexpected error has occurred');
        }
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await ApiService.deletePageMedia(
        props.pageId, { mediaData: props.currentPhoto, mediaType: 'logo' },
      );
      if (response === 'Success') {
        setPreview('');
        props.setLogo('');
        const pageIndex = props.pages.findIndex((el) => el.id === props.page.id);
        props.pages[pageIndex].pageLogo = null;
        props.dispatchPagesAccess(props.pages);
        props.dispatchPageSelected({ ...props.page, pageLogo: null });
      } else {
        toast.error('Error occured while deleting image');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const closeCropDialog = () => {
    setCropper(defaultCropperState);
    if (!IsEmpty(inputRef.current)) {
      inputRef.current.value = '';
    }
  };

  const handleSave = async (file) => {
    try {
      setLoading(true);
      if (!IsEmpty(file)) {
        const uri = await getFileDataUrl(file);
        setPreview(uri);

        if (!IsEmpty(props.currentPhoto)) {
          await handleDelete(); // delete the current one
        }
        await handleUpload(file); // upload the new image
      }
    } catch (err) {
      toast.error(err.message);
      console.log('Error at PageLogo.handleSave():', err);
    } finally {
      closeCropDialog();
      setLoading(false);
    }
  };

  useEffect(() => {
    setPreview(props.currentPhoto || '');
  }, [props]);

  return (
    <>
      {loading && <OverLoading />}

      {preview && (
        <>
          <div className="d-flex align-items-center justify-content-center text-center mx-auto" style={{ background: Colours.Black, overflow: 'hidden', position: 'relative', width: 200, height: 200, border: `1px solid ${Colours.Gray3}`, borderRadius: 200 }}>
            <img src={preview} alt="logo" className="d-block mx-auto" style={{ width: 200, height: 'auto', maxWidth: '100%' }} />
          </div>
        </>
      )}

      <label htmlFor="file-selector" className="d-block mx-auto">
        <Input
          inputRef={inputRef}
          id="file-selector"
          type="file"
          inputProps={{ multiple: false, accept: IMAGE_FORMATS }}
          onChange={handleImageChange}
          style={{ display: 'none' }}
          disabled={props.disabled}
        />

        {!preview ? (
          <>
            <Tooltip
              title={!props.disabled ? '' : 'Permission Denied'}
              isLink
            >
              <div
                className="d-flex mx-auto text-center"
                style={{
                  cursor:     !props.disabled && 'pointer',
                  marginTop:  10,
                  alignItems: 'center',
                  width:      200,
                  height:     200,
                }}
              >
                <div style={{ width: '100%' }}>
                  <Icons.Impression fontSize={125} colour={Colours.Gray5} />
                  <MText text="Upload Logo" colour={Colours.Gray5} />
                </div>
              </div>
            </Tooltip>
          </>
        )
          : (
            <>
              <div className="d-block mx-auto mt-3 text-center" style={{ cursor: 'pointer' }}>
                <Tooltip
                  title={!props.disabled ? '' : 'Permission Denied'}
                  isLink
                >
                  <MText text="Change Logo" colour={Colours.Gray5} />
                </Tooltip>
              </div>
            </>
          )}
      </label>

      <Dialog
        fullScreen
        open={cropper.isCropping}
        onClose={closeCropDialog}
      >
        <PhotoCropper
          width={4}
          height={3}
          cropPixelWidth={400}
          cropPixelHeight={400}
          outputPixelWidth={900}
          outputPixelHeight={900}
          type="upload"
          label="Logo"
          src={cropper.src}
          file={cropper.file}
          handleSave={handleSave}
          handleClose={closeCropDialog}
          isCropping={cropper.isCropping}
          uploading={loading}
        />
      </Dialog>
    </>
  );
};

PageLogo.propTypes = {
  pageId:               PropTypes.number.isRequired,
  currentPhoto:         PropTypes.string.isRequired,
  setLogo:              PropTypes.func.isRequired,
  page:                 PropTypes.shape(Object).isRequired,
  pages:                PropTypes.arrayOf(PropTypes.shape).isRequired,
  dispatchPageSelected: PropTypes.func.isRequired,
  dispatchPagesAccess:  PropTypes.func.isRequired,
  disabled:             PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  page:  state.page.selected,
  pages: state.page.pages,

});

const mapDispatchToProps = (dispatch) => ({
  dispatchPageSelected: (page) => pageAction.pageSelected(dispatch, page),
  dispatchPagesAccess:  (pages) => pageAction.pagesAccess(dispatch, pages),

});

export default connect(mapStateToProps, mapDispatchToProps)(PageLogo);
